import React, { useState, useEffect, useMemo } from 'react';
import {
  UserPlus,
  Zap,
  Users,
  Loader2,
  Check,
  AlertCircle,
  AlertTriangle,
  Wallet,
  Eye,
  EyeOff,
  Shield,
  X,
  Trash2,
  Activity,
  Copy,
} from 'lucide-react';
import { CustomDropdown } from '../../components/CustomDropdown';
import { DataToolbar } from '../../components/common/DataToolbar';
import { DataStateMessage } from '../../components/common/DataStateMessage';
import { Pagination } from '../../components/common/Pagination';
import { IProxy, IAccountDataResponse } from '@tool-no-hu/shared/types';
import {
  bulkCreateAccountsService,
  createRandomAccountService,
  loginAndAddAccountService,
  getAccountsService,
  getAccountsPaginatedService,
  updateAccountProxyService,
  deleteAccountService,
} from '../../service/account';
import { getProxiesService, checkProxyHealthService } from '../../service/proxy';
import { getGroupsService } from '../../service/group';
import { showToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import type { IGroupData } from '@tool-no-hu/shared/types';

// Username Sunwin: 6–20 ký tự, chỉ chữ/số/gạch dưới — khớp validate ở server
const USERNAME_PATTERN = /^[a-zA-Z0-9_]{6,20}$/;

// ─── Types ────────────────────────────────────────────────────────────

interface TableRow {
  index: number;
  proxy: string;
  status: 'idle' | 'creating' | 'success' | 'failed';
  username?: string;
  password?: string;
  displayname?: string;
  error?: string;
}

// ─── Accounts Page ───────────────────────────────────────────────────

export const Accounts: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'manual' | 'bulk'>('manual');

  // ── Manual tab state — 2 forms: Tạo + Đăng nhập ──────────────────
  const [createUsername, setCreateUsername] = useState('');
  const [createDisplayName, setCreateDisplayName] = useState('');
  const [createProxy, setCreateProxy] = useState('');
  const [createProxyList, setCreateProxyList] = useState<string[]>([]);
  const [createProxyHealth, setCreateProxyHealth] = useState<Record<string, 'alive' | 'dead'>>({});
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createResult, setCreateResult] = useState<{
    username: string;
    displayName: string;
    password: string;
  } | null>(null);

  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginShowPassword, setLoginShowPassword] = useState(false);
  const [loginProxy, setLoginProxy] = useState('');
  const [loginProxyList, setLoginProxyList] = useState<string[]>([]);
  const [loginProxyHealth, setLoginProxyHealth] = useState<Record<string, 'alive' | 'dead'>>({});
  const [loginSubmitting, setLoginSubmitting] = useState(false);

  const [createChecking, setCreateChecking] = useState(false);
  const [loginChecking, setLoginChecking] = useState(false);

  // ── Bulk tab state ─────────────────────────────────────────────────
  const [dbProxies, setDbProxies] = useState<IProxy[]>([]);
  const [loadingProxies, setLoadingProxies] = useState(true);
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [isTableVisible, setIsTableVisible] = useState(false);
  const [rows, setRows] = useState<TableRow[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Accounts list state ────────────────────────────────────────────
  const [accountsList, setAccountsList] = useState<IAccountDataResponse[]>([]);
  const [accountsPage, setAccountsPage] = useState(1);
  const [accountsTotalPages, setAccountsTotalPages] = useState(1);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [accountsSearch, setAccountsSearch] = useState('');
  const [accountsSearchInput, setAccountsSearchInput] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // ── Groups state (for delete eligibility check) ─────────────────────
  const [groupsList, setGroupsList] = useState<IGroupData[]>([]);

  // ── Proxy editing in table state ────────────────────────────────────
  const [editingProxyAccountId, setEditingProxyAccountId] = useState<string | null>(null);
  const [editingProxyValue, setEditingProxyValue] = useState('No Proxy');

  // ── Hiện/ẩn mật khẩu từng account trong bảng ────────────────────────
  const [revealedPasswords, setRevealedPasswords] = useState<Set<string>>(new Set());

  const togglePasswordVisible = (accountId: string) => {
    setRevealedPasswords((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const copyPassword = async (password: string) => {
    try {
      await navigator.clipboard.writeText(password);
      showToast.success('Đã copy mật khẩu');
    } catch {
      showToast.error('Trình duyệt chặn copy — hãy bấm hiện rồi copy tay');
    }
  };

  const confirm = useConfirm();

  // ── Shared helpers ─────────────────────────────────────────────────

  /**
   * accountId từ API là object đã populate ({ _id, username }), nhưng các optimistic
   * update trong trang này gán vào id dạng string. Chuẩn hoá về id string để mọi
   * phép so sánh "proxy này thuộc account nào" đều đúng với cả 2 dạng.
   */
  const getProxyAccountId = (proxy: IProxy): string | null => {
    const raw = proxy.accountId;
    if (!raw) return null;
    if (typeof raw === 'string') return raw;
    return raw._id ? String(raw._id) : null;
  };

  /**
   * Proxy còn trống — derive từ dbProxies chứ không giữ state riêng, để mọi thay đổi
   * (tạo/xoá tài khoản, đổi proxy) phản ánh ngay vào số lượng ở tab "Tạo hàng loạt".
   */
  const availableProxies = useMemo(
    () => dbProxies.filter((p) => !getProxyAccountId(p)),
    [dbProxies],
  );

  const fetchProxies = async () => {
    setLoadingProxies(true);
    try {
      const list = await getProxiesService();
      setDbProxies(list);
      const available = list.filter((p) => !getProxyAccountId(p));
      const availableValues = available.map((p) => p.value);
      const persistedHealth = available.reduce<Record<string, 'alive' | 'dead'>>((acc, proxy) => {
        if (proxy.alive === true) acc[proxy.value] = 'alive';
        if (proxy.alive === false) acc[proxy.value] = 'dead';
        return acc;
      }, {});

      // Initialize both forms with full available list (each form has its own list)
      setCreateProxyList(availableValues);
      setLoginProxyList(availableValues);
      setCreateProxyHealth(persistedHealth);
      setLoginProxyHealth(persistedHealth);
      if (available.length > 0) {
        setSelectedCount(Math.min(available.length, 10));
        const first = available[0]!;
        if (!createProxy) setCreateProxy(first.value);
        if (!loginProxy) setLoginProxy(first.value);
      } else {
        setSelectedCount(0);
      }
    } catch (err) {
      console.error('Failed to load proxies:', err);
    } finally {
      setLoadingProxies(false);
    }
  };

  // ── Proxy health helpers (per form) ────────────────────────────────
  const checkProxy = async (
    proxy: string,
    healthMap: Record<string, 'alive' | 'dead'>,
    setHealthMap: React.Dispatch<React.SetStateAction<Record<string, 'alive' | 'dead'>>>,
    setChecking: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!proxy) return;
    setChecking(true);
    try {
      const alive = await checkProxyHealthService(proxy);
      const nextStatus = alive ? 'alive' : 'dead';
      setHealthMap({ ...healthMap, [proxy]: nextStatus });
      setDbProxies((prev) =>
        prev.map((p) =>
          p.value === proxy ? { ...p, alive, lastChecked: new Date() } : p,
        ),
      );
    } catch (err) {
      showToast.error('Không thể check proxy');
    } finally {
      setChecking(false);
    }
  };

  /**
   * Sort proxy list theo health: live lên đầu, dead xuống cuối, chưa check ở giữa.
   * Dùng healthMap riêng cho từng form.
   */
  const getSortedProxyList = (
    list: string[],
    healthMap: Record<string, 'alive' | 'dead'>,
  ): string[] => {
    return [...list].sort((a, b) => {
      const aH = healthMap[a];
      const bH = healthMap[b];
      if (aH === 'alive' && bH !== 'alive') return -1;
      if (aH !== 'alive' && bH === 'alive') return 1;
      if (aH === 'dead' && bH !== 'dead') return 1;
      if (aH !== 'dead' && bH === 'dead') return -1;
      return 0;
    });
  };

  /**
   * Build proxy dropdown options với dot indicator (🟢 alive, 🔴 dead, ⚪ chưa check).
   * Dùng healthMap riêng cho từng form.
   */
  const getProxyOptions = (
    list: string[],
    healthMap: Record<string, 'alive' | 'dead'>,
  ): { value: string; label: string }[] => {
    return getSortedProxyList(list, healthMap).map((p) => {
      const h = healthMap[p];
      const dot = h === 'alive' ? '🟢 ' : h === 'dead' ? '🔴 ' : '⚪ ';
      return { value: p, label: `${dot}${p}` };
    });
  };

  const fetchAccountsList = async () => {
    setLoadingAccounts(true);
    try {
      const result = await getAccountsPaginatedService(accountsPage, 10, accountsSearch);
      if (result) {
        setAccountsList(result.items);
        setAccountsTotalPages(result.totalPages);
        setAccountsTotal(result.total);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const data = await getGroupsService({ silent: true });
      setGroupsList(data as IGroupData[]);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  };

  useEffect(() => {
    fetchProxies();
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchAccountsList();
  }, [accountsPage, accountsSearch]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (accountsSearchInput !== accountsSearch) {
        setAccountsSearch(accountsSearchInput);
        setAccountsPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [accountsSearchInput]);

  // ── Manual tab handlers — 2 forms ─────────────────────────────────

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createUsername.trim() || !createDisplayName.trim() || !createProxy || createSubmitting)
      return;
    if (!USERNAME_PATTERN.test(createUsername.trim())) {
      showToast.error('Username phải 6–20 ký tự, chỉ gồm chữ, số hoặc _');
      return;
    }
    setCreateSubmitting(true);
    try {
      const result = await createRandomAccountService({
        username: createUsername.trim(),
        displayName: createDisplayName.trim(),
        proxy: createProxy,
      });
      if (result) {
        setCreateResult({
          username: result.username,
          displayName: result.displayname,
          password: result.password,
        });
        showToast.success(`Đã tạo tài khoản "${result.username}" — vào Sảnh để đăng nhập`);
        // Optimistic update — prepend to accounts list, jump to page 1
        setAccountsList((prev) => [
          {
            _id: result._id,
            username: result.username,
            displayname: result.displayname,
            password: result.password,
            status: 'STOPPED',
            balance: 0,
            config: '',
            rounds: { total: 0, w: 0, l: 0, rate: 'N/A' },
            proxy: result.proxy,
            tokenTime: 'No Token',
            tokenWarning: false,
          } as IAccountDataResponse,
          ...prev,
        ]);
        setAccountsTotal((t) => t + 1);
        if (accountsPage !== 1) setAccountsPage(1);
        // Mark proxy as assigned in cache
        setDbProxies((prev) =>
          prev.map((p) => (p.value === result.proxy ? { ...p, accountId: result._id } : p)),
        );
        // Update create form's list and proxy — giữ createResult để user copy mật khẩu
        const nextList = createProxyList.filter((v) => v !== result.proxy);
        setCreateProxyList(nextList);
        setCreateUsername('');
        setCreateDisplayName('');
        setCreateProxy(nextList[0] || '');
        setCreateProxyHealth((prev) => {
          const { [result.proxy]: _drop, ...rest } = prev;
          return rest;
        });
      }
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginUsername.trim() || !loginPassword.trim() || !loginProxy || loginSubmitting) return;
    setLoginSubmitting(true);
    try {
      const result = await loginAndAddAccountService({
        username: loginUsername.trim(),
        password: loginPassword,
        proxy: loginProxy,
      });
      if (result) {
        showToast.success(`Đã thêm tài khoản "${result.username}" — vào Sảnh để đăng nhập`);
        setAccountsList((prev) => [
          {
            _id: result._id,
            username: result.username,
            displayname: result.displayname,
            password: loginPassword,
            status: 'STOPPED',
            balance: 0,
            config: '',
            rounds: { total: 0, w: 0, l: 0, rate: 'N/A' },
            proxy: result.proxy,
            tokenTime: 'No Token',
            tokenWarning: false,
          } as IAccountDataResponse,
          ...prev,
        ]);
        setAccountsTotal((t) => t + 1);
        if (accountsPage !== 1) setAccountsPage(1);
        // Mark proxy as assigned in cache
        setDbProxies((prev) =>
          prev.map((p) => (p.value === result.proxy ? { ...p, accountId: result._id } : p)),
        );
        // Update login form's list and proxy
        const nextList = loginProxyList.filter((v) => v !== result.proxy);
        setLoginProxyList(nextList);
        setLoginUsername('');
        setLoginPassword('');
        setLoginProxy(nextList[0] || '');
        setLoginProxyHealth((prev) => {
          const { [result.proxy]: _drop, ...rest } = prev;
          return rest;
        });
      }
    } finally {
      setLoginSubmitting(false);
    }
  };

  // ── Bulk tab handlers ──────────────────────────────────────────────

  const selectOptions: { value: number; label: string }[] = [];
  const totalAvailable = availableProxies.length;
  if (totalAvailable === 0) {
    selectOptions.push({ value: 0, label: '0 tài khoản (Sẵn có: 0 Proxy)' });
  } else {
    for (let count = 1; count <= totalAvailable; count += 1) {
      selectOptions.push({
        value: count,
        label: `${count} tài khoản (Sẵn có: ${totalAvailable} Proxy)`,
      });
    }
  }

  const handleConfirmCount = () => {
    if (selectedCount <= 0) {
      showToast.error('Vui lòng chọn số lượng lớn hơn 0');
      return;
    }

    const selectedProxies = availableProxies.slice(0, selectedCount);
    const initialRows = selectedProxies.map((p, idx) => ({
      index: idx + 1,
      proxy: p.value,
      status: 'idle' as const,
    }));

    setRows(initialRows);
    setIsTableVisible(true);
  };

  const handleStartCreation = async () => {
    const targets = rows.filter((r) => r.status === 'idle' || r.status === 'failed');
    if (targets.length === 0) return;

    setIsSubmitting(true);

    setRows((prev) =>
      prev.map((r) =>
        r.status === 'idle' || r.status === 'failed'
          ? { ...r, status: 'creating' as const }
          : r,
      ),
    );

    try {
      const proxiesArray = targets.map((t) => t.proxy);
      const results = await bulkCreateAccountsService(proxiesArray.length, proxiesArray);

      setRows((prev) =>
        prev.map((row) => {
          const res = results.find((item) => item.proxy === row.proxy);
          if (res) {
            return {
              ...row,
              status: res.success ? ('success' as const) : ('failed' as const),
              username: res.username,
              password: res.password,
              displayname: res.displayname,
              error: res.error,
            };
          }
          return row;
        }),
      );

      const successCount = results.filter((r) => r.success).length;
      if (successCount > 0) {
        showToast.success(`Tạo thành công ${successCount} tài khoản!`);
        await fetchProxies();
        await fetchAccountsList();
      }
    } catch (err: any) {
      showToast.error(err.message || 'Thao tác tạo tài khoản hàng loạt thất bại');
      setRows((prev) =>
        prev.map((r) => (r.status === 'creating' ? { ...r, status: 'failed' as const } : r)),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const failedRows = rows.filter((r) => r.status === 'failed');
  const successRows = rows.filter((r) => r.status === 'success');
  const allSucceeded = rows.length > 0 && successRows.length === rows.length;

  // ── Proxy change handler ───────────────────────────────────────────
  const handleChangeProxy = async (accountId: string, newProxy: string) => {
    if (!accountId) return;
    try {
      const result = await updateAccountProxyService(accountId, newProxy);
      if (result) {
        showToast.success('Đổi proxy thành công');
        // Optimistic update — update row + clear old proxy, set new proxy
        setAccountsList((prev) =>
          prev.map((a) => (a._id === accountId ? { ...a, proxy: newProxy } : a)),
        );
        setDbProxies((prev) =>
          prev.map((p) => {
            if (getProxyAccountId(p) === accountId) return { ...p, accountId: null };
            if (p.value === newProxy && newProxy !== 'No Proxy') return { ...p, accountId };
            return p;
          }),
        );
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Đổi proxy thất bại';
      showToast.error(msg);
    }
    setEditingProxyAccountId(null);
  };

  // ── Delete handler ─────────────────────────────────────────────────
  const canDeleteAccount = (acc: IAccountDataResponse): { allowed: boolean; reason?: string } => {
    if (!acc._id) return { allowed: false, reason: 'Không có ID' };
    const group = groupsList.find((g) =>
      (g.members as any[]).some((m) => {
        const memberId = typeof m === 'string' ? m : String(m._id || m.accountId);
        return memberId === acc._id;
      }),
    );
    if (!group) return { allowed: true };
    const hasRunning = (group.members as any[]).some(
      (m) => typeof m !== 'string' && m.status === 'RUNNING',
    );
    if (hasRunning) {
      return { allowed: false, reason: 'Nhóm đang hoạt động — hãy dừng group trước' };
    }
    return { allowed: true };
  };

  const handleDeleteAccount = async (acc: IAccountDataResponse) => {
    if (!acc._id) return;
    const isConfirmed = await confirm({
      title: 'Xoá tài khoản',
      message: `Bạn có chắc chắn muốn xoá tài khoản "${acc.username}"? Toàn bộ lịch sử nạp/rút và betlog của tài khoản này cũng bị xoá vĩnh viễn.`,
      type: 'danger',
    });
    if (!isConfirmed) return;
    try {
      const success = await deleteAccountService(acc._id);
      if (success) {
        showToast.success(`Đã xoá tài khoản "${acc.username}"`);
        // Optimistic update — filter from list, clear proxy cache
        setAccountsList((prev) => prev.filter((a) => a._id !== acc._id));
        setDbProxies((prev) =>
          prev.map((p) => (getProxyAccountId(p) === acc._id ? { ...p, accountId: null } : p)),
        );
        // Groups need refetch — BE removes account from subdoc, UI must reflect
        await fetchGroups();
      } else {
        showToast.error('Xoá tài khoản thất bại');
      }
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Có lỗi xảy ra khi xoá tài khoản');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────

  const TAB_CLASS = (active: boolean) =>
    `rounded-md px-4 py-2 text-[13px] font-bold transition-all ${
      active
        ? 'bg-[#3b82f6] text-white shadow-sm'
        : 'text-[#64748b] hover:text-[#e2e8f0]'
    }`;

  return (
    <div className="space-y-6 text-[#e2e8f0]">
      {/* Page Header */}
      <div className="mb-2 flex items-center gap-3.5">
        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-[#2d4a8a] bg-[#1e2a4a]">
          <Wallet size={20} className="text-[#3b82f6]" />
        </div>
        <div>
          <h1 className="m-0 text-[18px] font-bold text-[#e2e8f0]">Kho tài khoản</h1>
          <p className="m-0 mt-0.5 text-[13px] text-[#64748b]">
            Thêm và quản lý tài khoản Sunwin
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="inline-flex rounded-lg border border-[#252a38] bg-[#0d0f14] p-0.5">
        <button
          type="button"
          onClick={() => setActiveTab('manual')}
          className={TAB_CLASS(activeTab === 'manual')}
        >
          <UserPlus size={14} className="inline-block mr-1.5 align-text-top" />
          Nhập thủ công
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('bulk')}
          className={TAB_CLASS(activeTab === 'bulk')}
        >
          <Zap size={14} className="inline-block mr-1.5 align-text-top" />
          Tạo hàng loạt
        </button>
      </div>

      {/* ── Tab: Nhập thủ công ──────────────────────────────────────── */}
      {activeTab === 'manual' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* ── Card 1: Tạo tài khoản ───────────────────────────── */}
          <div className="rounded-xl border border-[#252a38] bg-[#161920] px-6 py-5">
            <h3 className="mb-1 text-[14px] font-bold text-[#e2e8f0]">Tạo tài khoản</h3>
            <p className="mb-4 text-[11px] text-[#64748b]">Tự nhập username + tên hiển thị. Mật khẩu do hệ thống sinh ngẫu nhiên và hiện sau khi tạo xong.</p>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                  Username <span className="text-[#ef4444]">*</span>
                </label>
                <input
                  type="text"
                  value={createUsername}
                  onChange={(e) => setCreateUsername(e.target.value)}
                  placeholder="Tên đăng nhập Sunwin"
                  required
                  autoComplete="off"
                  className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                />
                {createUsername.trim() && !USERNAME_PATTERN.test(createUsername.trim()) ? (
                  <p className="mt-1 text-[10px] text-[#ef4444]">
                    Username phải 6–20 ký tự, chỉ gồm chữ, số hoặc _
                  </p>
                ) : (
                  <p className="mt-1 text-[10px] text-[#64748b]">6–20 ký tự, chỉ gồm chữ, số hoặc _</p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                  Tên hiển thị <span className="text-[#ef4444]">*</span>
                </label>
                <input
                  type="text"
                  value={createDisplayName}
                  onChange={(e) => setCreateDisplayName(e.target.value)}
                  placeholder="Tên sẽ hiển thị trong game"
                  required
                  className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                />
              </div>

              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                  Proxy <span className="text-[#ef4444]">*</span>
                </label>
                <div className="flex items-center gap-1.5">
                  <div className="flex-1">
                    <CustomDropdown
                      value={createProxy}
                      onChange={setCreateProxy}
                      placeholder="-- Chọn proxy --"
                      className="w-full"
                      openId="accounts-create-proxy"
                      options={getProxyOptions(createProxyList, createProxyHealth)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => checkProxy(createProxy, createProxyHealth, setCreateProxyHealth, setCreateChecking)}
                    disabled={!createProxy || createChecking}
                    className="flex shrink-0 items-center gap-1 rounded-lg border border-[#252a38] bg-[#0d0f14] px-2.5 py-2.5 text-[11px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:cursor-not-allowed disabled:opacity-50"
                    title="Check proxy health"
                  >
                    {createChecking ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />}
                    Check
                  </button>
                </div>
                {createProxy && (
                  <p className="mt-1 text-[10px] text-[#64748b]">
                    {createProxyHealth[createProxy] === 'alive' ? (
                      <span className="text-[#22c55e]">🟢 Proxy còn sống — sẵn sàng tạo</span>
                    ) : createProxyHealth[createProxy] === 'dead' ? (
                      <span className="text-[#ef4444]">🔴 Proxy chết — KHÔNG nên dùng</span>
                    ) : (
                      <span>⚪ Chưa check — nhấn "Check" trước khi tạo để tránh lỗi SSL</span>
                    )}
                  </p>
                )}
                {createProxyList.length === 0 && (
                  <p className="mt-1 text-[10px] text-[#ef4444]">
                    Chưa có proxy khả dụng. Vui lòng thêm proxy trước.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={
                  createSubmitting ||
                  !createUsername.trim() ||
                  !USERNAME_PATTERN.test(createUsername.trim()) ||
                  !createDisplayName.trim() ||
                  !createProxy
                }
                className="flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {createSubmitting && <Loader2 size={14} className="animate-spin" />}
                {createSubmitting ? 'Đang tạo...' : 'Tạo tài khoản'}
              </button>
            </form>

            {createResult && (
              <div className="mt-4 rounded-lg border border-[#22c55e]/40 bg-[#22c55e]/5 px-4 py-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#22c55e]">
                    Tài khoản vừa tạo — lưu lại mật khẩu
                  </span>
                  <button
                    type="button"
                    onClick={() => setCreateResult(null)}
                    className="text-[#64748b] hover:text-[#e2e8f0]"
                    title="Đóng"
                  >
                    <X size={14} />
                  </button>
                </div>
                <dl className="space-y-1 text-[12px]">
                  <div className="flex gap-2">
                    <dt className="w-24 shrink-0 text-[#64748b]">Username</dt>
                    <dd className="font-semibold text-[#e2e8f0]">{createResult.username}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-24 shrink-0 text-[#64748b]">Tên hiển thị</dt>
                    <dd className="font-semibold text-[#e2e8f0]">{createResult.displayName}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-24 shrink-0 text-[#64748b]">Mật khẩu</dt>
                    <dd className="font-mono font-semibold text-[#f59e0b]">{createResult.password}</dd>
                  </div>
                </dl>
              </div>
            )}
          </div>

          {/* ── Card 2: Đăng nhập tài khoản ──────────────────────── */}
          <div className="rounded-xl border border-[#252a38] bg-[#161920] px-6 py-5">
            <h3 className="mb-1 text-[14px] font-bold text-[#e2e8f0]">Đăng nhập tài khoản</h3>
            <p className="mb-4 text-[11px] text-[#64748b]">Validate Sunwin credentials có sẵn. Nếu OK sẽ lưu local DB.</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                  Username <span className="text-[#ef4444]">*</span>
                </label>
                <input
                  type="text"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  placeholder="Username Sunwin đã đăng ký"
                  required
                  className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                />
              </div>

              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                  Mật khẩu <span className="text-[#ef4444]">*</span>
                </label>
                <div className="relative">
                  <input
                    type={loginShowPassword ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Mật khẩu Sunwin"
                    required
                    className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 pr-10 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                  />
                  <button
                    type="button"
                    onClick={() => setLoginShowPassword(!loginShowPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#e2e8f0]"
                    tabIndex={-1}
                  >
                    {loginShowPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
                  Proxy <span className="text-[#ef4444]">*</span>
                </label>
                <div className="flex items-center gap-1.5">
                  <div className="flex-1">
                    <CustomDropdown
                      value={loginProxy}
                      onChange={setLoginProxy}
                      placeholder="-- Chọn proxy --"
                      className="w-full"
                      openId="accounts-login-proxy"
                      options={getProxyOptions(loginProxyList, loginProxyHealth)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => checkProxy(loginProxy, loginProxyHealth, setLoginProxyHealth, setLoginChecking)}
                    disabled={!loginProxy || loginChecking}
                    className="flex shrink-0 items-center gap-1 rounded-lg border border-[#252a38] bg-[#0d0f14] px-2.5 py-2.5 text-[11px] font-bold text-[#94a3b8] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0] disabled:cursor-not-allowed disabled:opacity-50"
                    title="Check proxy health"
                  >
                    {loginChecking ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />}
                    Check
                  </button>
                </div>
                {loginProxy && (
                  <p className="mt-1 text-[10px] text-[#64748b]">
                    {loginProxyHealth[loginProxy] === 'alive' ? (
                      <span className="text-[#22c55e]">🟢 Proxy còn sống</span>
                    ) : loginProxyHealth[loginProxy] === 'dead' ? (
                      <span className="text-[#ef4444]">🔴 Proxy chết</span>
                    ) : (
                      <span>⚪ Chưa check</span>
                    )}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loginSubmitting || !loginUsername.trim() || !loginPassword.trim() || !loginProxy}
                className="flex items-center gap-1.5 rounded-lg bg-[#22c55e] px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#16a34a] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loginSubmitting && <Loader2 size={14} className="animate-spin" />}
                {loginSubmitting ? 'Đang xác thực...' : 'Đăng nhập & Thêm'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Tab: Tạo hàng loạt ──────────────────────────────────────── */}
      {activeTab === 'bulk' && (
        <>
          {/* Selector */}
          <div className="rounded-xl border border-[#252a38] bg-[#161920] px-6 py-5">
            <label className="mb-3 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
              Số lượng tài khoản cần tạo
            </label>

            {loadingProxies ? (
              <div className="flex items-center gap-2 text-[12px] font-medium text-[#64748b]">
                <Loader2 size={14} className="animate-spin text-[#64748b]" />
                Đang tải danh sách proxy...
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2.5">
                  <CustomDropdown
                    value={selectedCount.toString()}
                    onChange={(v) => setSelectedCount(parseInt(v, 10))}
                    disabled={isSubmitting}
                    searchable={false}
                    placeholder="-- Chọn số lượng --"
                    className="min-w-[280px] flex-1"
                    options={selectOptions.map((opt) => ({
                      value: opt.value.toString(),
                      label: opt.label,
                    }))}
                  />
                  <button
                    type="button"
                    onClick={handleConfirmCount}
                    disabled={isSubmitting || totalAvailable === 0 || selectedCount === 0}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#3b82f6] px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Zap size={14} />
                    Xác nhận
                  </button>
                </div>

                <p className="mt-2.5 text-[11px] text-[#475569]">
                  Sẵn có:{' '}
                  <span className="font-mono font-bold text-[#7dd3fc]">
                    {totalAvailable}
                  </span>{' '}
                  proxy chưa được gán tài khoản
                </p>
              </>
            )}
          </div>

          {/* Result table */}
          {isTableVisible && (
            <div className="overflow-hidden rounded-xl border border-[#252a38] bg-[#161920]">
              <div className="flex items-center justify-between border-b border-[#252a38] px-5 py-4">
                <div className="flex items-center gap-2 text-[14px] font-semibold text-[#e2e8f0]">
                  <Zap size={16} />
                  <span>Danh sách tài khoản</span>
                  <span className="rounded-full bg-[#1e2a4a] px-2 py-0.5 text-[11px] font-bold text-[#3b82f6]">
                    {rows.length}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  {successRows.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#14532d] bg-[#0d1f14] px-2 py-0.5 font-bold text-[#22c55e]">
                      <Check size={10} />
                      {successRows.length} thành công
                    </span>
                  )}
                  {failedRows.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#7f1d1d] bg-[#1c0a0a] px-2 py-0.5 font-bold text-[#ef4444]">
                      <AlertCircle size={10} />
                      {failedRows.length} thất bại
                    </span>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="bg-[#10131a]">
                      <th className="w-[60px] px-5 py-2.5 text-center text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                        STT
                      </th>
                      <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                        Proxy
                      </th>
                      <th className="px-5 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                        Kết quả
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.proxy}
                        className="border-t border-[#1e2235] transition-colors hover:bg-[#1c2030]"
                      >
                        <td className="px-5 py-3 text-center font-mono text-[12px] font-bold text-[#64748b]">
                          {row.index}
                        </td>
                        <td className="px-5 py-3 font-mono text-[12px] text-[#7dd3fc]">
                          {row.proxy}
                        </td>
                        <td className="px-5 py-3">
                          {row.status === 'idle' && (
                            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#64748b]">
                              Chờ tạo...
                            </span>
                          )}
                          {row.status === 'creating' && (
                            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#3b82f6]">
                              <Loader2 size={13} className="animate-spin" />
                              Đang tạo tài khoản...
                            </span>
                          )}
                          {row.status === 'success' && (
                            <div className="space-y-1.5">
                              <span className="inline-flex items-center gap-1 rounded border border-[#14532d] bg-[#0d1f14] px-2 py-0.5 text-[10px] font-bold text-[#22c55e]">
                                <Check size={10} />
                                Thành công
                              </span>
                              <div className="space-y-0.5 font-mono text-[11px] text-[#94a3b8]">
                                <div>
                                  Tên đăng nhập:{' '}
                                  <strong className="font-bold text-[#e2e8f0]">{row.username}</strong>
                                </div>
                                <div>
                                  Mật khẩu:{' '}
                                  <strong className="font-bold text-[#e2e8f0]">{row.password}</strong>
                                </div>
                                <div>
                                  Tên hiển thị:{' '}
                                  <strong className="font-bold text-[#e2e8f0]">{row.displayname}</strong>
                                </div>
                              </div>
                            </div>
                          )}
                          {row.status === 'failed' && (
                            <div className="space-y-1.5">
                              <span className="inline-flex items-center gap-1 rounded border border-[#7f1d1d] bg-[#1c0a0a] px-2 py-0.5 text-[10px] font-bold text-[#ef4444]">
                                <AlertCircle size={10} />
                                Thất bại
                              </span>
                              <div className="font-semibold text-[12px] text-[#ef4444]">
                                {row.error}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer action */}
              <div className="flex items-center justify-end border-t border-[#252a38] bg-[#10131a] px-5 py-4">
                {allSucceeded ? (
                  <button
                    type="button"
                    disabled
                    className="flex items-center gap-1.5 rounded-lg border border-[#14532d] bg-[#0d1f14] px-5 py-2.5 text-[13px] font-bold text-[#22c55e] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <Check size={14} />
                    Tạo thành công toàn bộ
                  </button>
                ) : failedRows.length > 0 ? (
                  <button
                    type="button"
                    onClick={handleStartCreation}
                    disabled={isSubmitting}
                    className="flex items-center gap-1.5 rounded-lg bg-[#f59e0b] px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#d97706] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmitting && <Loader2 size={14} className="animate-spin" />}
                    Tạo lại {failedRows.length} tài khoản thất bại
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartCreation}
                    disabled={isSubmitting || rows.length === 0}
                    className="flex items-center gap-1.5 rounded-lg bg-[#3b82f6] px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmitting && <Loader2 size={14} className="animate-spin" />}
                    Bắt đầu tạo
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!loadingProxies && totalAvailable === 0 && (
            <div className="flex flex-col items-center gap-2 rounded-xl border border-[#252a38] bg-[#161920] px-5 py-12 text-[#334155]">
              <AlertTriangle size={32} className="text-[#252a38]" />
              <p className="text-[14px] font-semibold text-[#475569]">
                Chưa có proxy khả dụng
              </p>
              <span className="text-[12px] text-[#334155]">
                Thêm proxy chưa được gán tài khoản để bắt đầu tạo hàng loạt
              </span>
            </div>
          )}
        </>
      )}

      {/* ── Danh sách tài khoản ──────────────────────────────────────── */}
      <div className="overflow-hidden rounded-xl border border-[#252a38] bg-[#161920]">
        <DataToolbar
          icon={<Users size={16} />}
          title="Danh sách tài khoản"
          count={accountsTotal}
          searchValue={accountsSearchInput}
          onSearchChange={setAccountsSearchInput}
          searchPlaceholder="Tìm theo username hoặc tên hiển thị..."
        />

        <DataStateMessage
          loading={loadingAccounts}
          empty={!loadingAccounts && accountsList.length === 0 ? 'Chưa có tài khoản' : null}
          emptyHint={
            !loadingAccounts && accountsList.length === 0
              ? 'Sử dụng tab "Nhập thủ công" hoặc "Tạo hàng loạt" để thêm tài khoản'
              : undefined
          }
          emptyIcon={<Users size={32} className="text-[#252a38]" />}
        />

        {!loadingAccounts && accountsList.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-[#10131a]">
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Username
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Tên hiển thị
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Mật khẩu
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Proxy
                  </th>
                  <th className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Ngày tạo
                  </th>
                  <th className="px-5 py-2.5 text-center text-[10px] font-bold uppercase tracking-[0.1em] text-[#475569]">
                    Hành động
                  </th>
                </tr>
              </thead>
              <tbody>
                {accountsList.map((acc) => {
                  const proxyShort = acc.proxy ? acc.proxy.split(':').slice(0, 2).join(':') : '';
                  const isEditing = editingProxyAccountId === acc._id;
                  const isPasswordVisible = revealedPasswords.has(acc._id || '');
                  // All unassigned proxies + current proxy
                  const editableProxies = [
                    { value: 'No Proxy', label: 'No Proxy' },
                    ...dbProxies
                      .filter((p) => {
                        const assignedTo = getProxyAccountId(p);
                        return !assignedTo || assignedTo === acc._id;
                      })
                      .map((p) => ({ value: p.value, label: p.value })),
                  ];

                  return (
                    <tr
                      key={acc._id}
                      className="border-t border-[#1e2235] transition-colors hover:bg-[#1c2030]"
                    >
                      <td className="px-5 py-3 font-mono text-[12px] font-bold text-[#e2e8f0]">
                        {acc.username}
                      </td>
                      <td className="px-5 py-3 text-[12px] text-[#94a3b8]">
                        {acc.displayname}
                      </td>
                      <td className="px-5 py-3">
                        {acc.password ? (
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[12px] text-[#e2e8f0]">
                              {isPasswordVisible ? acc.password : '•'.repeat(acc.password.length)}
                            </span>
                            <button
                              type="button"
                              onClick={() => togglePasswordVisible(acc._id || '')}
                              className="rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#e2e8f0]"
                              title={isPasswordVisible ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
                            >
                              {isPasswordVisible ? <EyeOff size={12} /> : <Eye size={12} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => copyPassword(acc.password || '')}
                              className="rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#3b82f6]"
                              title="Copy mật khẩu"
                            >
                              <Copy size={12} />
                            </button>
                          </div>
                        ) : (
                          <span className="text-[12px] text-[#475569]">-</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <CustomDropdown
                              value={editingProxyValue}
                              onChange={setEditingProxyValue}
                              placeholder="-- Chọn proxy --"
                              className="min-w-[200px]"
                              openId={`edit-proxy-${acc._id}`}
                              options={editableProxies}
                            />
                            <button
                              type="button"
                              onClick={() => handleChangeProxy(acc._id || '', editingProxyValue)}
                              className="rounded bg-[#3b82f6] px-2 py-1.5 text-[10px] font-bold text-white hover:bg-[#2563eb]"
                            >
                              <Check size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingProxyAccountId(null)}
                              className="rounded bg-[#252a38] px-2 py-1.5 text-[10px] font-bold text-[#94a3b8] hover:bg-[#1e2235]"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <Shield size={12} className="text-[#334155]" />
                            <span className="font-mono text-[11px] text-[#94a3b8]">
                              {proxyShort || '-'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-[12px] text-[#64748b]">
                        {acc._id ? new Date(parseInt(acc._id.substring(0, 8), 16) * 1000).toLocaleDateString('vi-VN') : '-'}
                      </td>
                      <td className="px-5 py-3 text-center">
                        {isEditing ? null : (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingProxyAccountId(acc._id || null);
                                setEditingProxyValue(acc.proxy || 'No Proxy');
                              }}
                              className="rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#3b82f6]"
                              title="Đổi proxy"
                            >
                              <Shield size={14} />
                            </button>
                            {(() => {
                              const del = canDeleteAccount(acc);
                              return (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAccount(acc)}
                                  disabled={!del.allowed}
                                  className="rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#ef4444] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#64748b]"
                                  title={del.allowed ? 'Xoá tài khoản' : del.reason}
                                >
                                  <Trash2 size={14} />
                                </button>
                              );
                            })()}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={accountsPage}
          totalPages={accountsTotalPages}
          onPageChange={setAccountsPage}
          loading={loadingAccounts}
        />
      </div>
    </div>
  );
};
